import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Type, Code2, Image, Monitor, Smartphone, RefreshCw, Trash2, AlertTriangle, Clock } from 'lucide-react';
import { HeadlineOverlay } from '@/store/videoFeatures/types/headline-overlay';
import { validateTiming } from '@/utils/headline-validators';
import { useDebounce } from '@/hooks/use-debounce';
import { PositionCard } from '../action-button/shared/PositionCard';
import { ButtonTimingCard } from '../action-button/shared/ButtonTimingCard';
import { TimePicker } from '@/components/ui/TimePicker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { HexColorPicker } from 'react-colorful';
import { cn } from '@/lib/utils';

const formatColorValue = (color: string): string => {
  if (color.startsWith('rgba')) {
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) return `${match[1]}, ${match[2]}, ${match[3]}`;
  }
  return color.toUpperCase();
};

// Utility functions for time conversion
const formatSecondsToTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const parseTimeToSeconds = (timeStr: string): number => {
  const parts = timeStr.split(':');
  if (parts.length !== 2) return 0;
  const mins = parseInt(parts[0], 10) || 0;
  const secs = parseInt(parts[1], 10) || 0;
  return mins * 60 + secs;
};

interface HeadlineConfigTabProps {
  headlineOverlay: HeadlineOverlay;
  updateHeadlineOverlay: (key: keyof HeadlineOverlay, value: any) => void;
  updateHeadlineOverlayBatch: (updates: Partial<HeadlineOverlay>) => void;
}

export const HeadlineConfigTab: React.FC<HeadlineConfigTabProps> = ({
  headlineOverlay,
  updateHeadlineOverlay,
  updateHeadlineOverlayBatch,
}) => {
  useLanguage();
  // Local state for real-time input with debounce
  const [localHeadlineText, setLocalHeadlineText] = useState(headlineOverlay.headline.text);
  const [localSubheadlineText, setLocalSubheadlineText] = useState(headlineOverlay.subheadline.text);
  const [localHtmlCode, setLocalHtmlCode] = useState(headlineOverlay.htmlMode.code);
  // Estado local (string) dos inputs de tamanho — permite o usuário apagar
  // tudo e digitar de novo sem que o fallback interrompa a digitação.
  // A propagação para o state global só acontece quando o valor é um número
  // válido; o onBlur normaliza (clamp 10..80) se o usuário sair com campo
  // vazio ou fora do range.
  const [localHeadlineSize, setLocalHeadlineSize] = useState<string>(
    String(headlineOverlay.headline.size ?? 24),
  );
  const [localSubheadlineSize, setLocalSubheadlineSize] = useState<string>(
    String(headlineOverlay.subheadline.size ?? 16),
  );
  const [htmlError, setHtmlError] = useState<string | null>(null);
  const [timingError, setTimingError] = useState<string | null>(null);

  // Debounced values for performance
  const debouncedHeadlineText = useDebounce(localHeadlineText, 300);
  const debouncedSubheadlineText = useDebounce(localSubheadlineText, 300);
  const debouncedHtmlCode = useDebounce(localHtmlCode, 500);

  // Update main state when debounced values change
  useEffect(() => {
    if (debouncedHeadlineText !== headlineOverlay.headline.text) {
      updateHeadline('text', debouncedHeadlineText);
    }
  }, [debouncedHeadlineText]);

  useEffect(() => {
    if (debouncedSubheadlineText !== headlineOverlay.subheadline.text) {
      updateSubheadline('text', debouncedSubheadlineText);
    }
  }, [debouncedSubheadlineText]);

  // Sincronizar localHtmlCode quando o dado vem do backend (loadExistingVideo)
  useEffect(() => {
    const backendCode = headlineOverlay.htmlMode.code || '';
    if (backendCode !== localHtmlCode) {
      setLocalHtmlCode(backendCode);
    }
  }, [headlineOverlay.htmlMode.code]);

  // Sincronizar os inputs de tamanho quando o dado externo mudar (load,
  // seleção de template, etc.) — só atualiza se for diferente do que o
  // usuário tem no campo, evitando "pular" enquanto digita.
  useEffect(() => {
    const current = parseInt(localHeadlineSize, 10);
    if (current !== headlineOverlay.headline.size) {
      setLocalHeadlineSize(String(headlineOverlay.headline.size ?? 24));
    }
  }, [headlineOverlay.headline.size]);

  useEffect(() => {
    const current = parseInt(localSubheadlineSize, 10);
    if (current !== headlineOverlay.subheadline.size) {
      setLocalSubheadlineSize(String(headlineOverlay.subheadline.size ?? 16));
    }
  }, [headlineOverlay.subheadline.size]);

  // Clamp + propagação "final" no blur. Durante o typing permitimos
  // string vazia no input (sem empurrar 24 de volta) — só normaliza quando
  // o usuário sai do campo ou envia um valor fora do range.
  const MIN_PX = 10;
  const MAX_PX = 80;
  const commitSizeOnBlur = (
    raw: string,
    defaultPx: number,
    apply: (v: number) => void,
    setLocal: (v: string) => void,
  ) => {
    const parsed = parseInt(raw, 10);
    let next = defaultPx;
    if (Number.isFinite(parsed)) {
      next = Math.min(MAX_PX, Math.max(MIN_PX, parsed));
    }
    setLocal(String(next));
    apply(next);
  };


  const updateHeadline = (key: string, value: any) => {
    updateHeadlineOverlay('headline', {
      ...headlineOverlay.headline,
      [key]: value
    });
  };

  const updateSubheadline = (key: string, value: any) => {
    updateHeadlineOverlay('subheadline', {
      ...headlineOverlay.subheadline,
      [key]: value
    });
  };

  const updateTiming = (key: string | Record<string, any>, value?: any) => {
    const updates = typeof key === 'object' ? key : { [key]: value };
    const newTiming = {
      ...headlineOverlay.timing,
      ...updates,
    };

    // Validate timing
    const validation = validateTiming(newTiming.start, newTiming.end);
    if (!validation.valid) {
      setTimingError(validation.error || null);
    } else {
      setTimingError(null);
    }

    updateHeadlineOverlay('timing', newTiming);
  };

  const updateHtmlMode = (key: string, value: any) => {
    updateHeadlineOverlay('htmlMode', {
      ...headlineOverlay.htmlMode,
      [key]: value
    });
  };

  const updateImageMode = (key: string, value: any) => {
    updateHeadlineOverlay('imageMode', {
      ...headlineOverlay.imageMode,
      [key]: value
    });
  };

  const updateTextMode = (key: string, value: any) => {
    updateHeadlineOverlay('textMode', {
      ...headlineOverlay.textMode,
      [key]: value
    });
  };


  const handleDesktopImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      updateImageMode('desktopImage', result);
    };
    reader.readAsDataURL(file);
  };

  const handleMobileImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      updateImageMode('mobileImage', result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto min-h-0 premium-scroll">
        <div className="space-y-6">

          {/* Modo de Exibição */}
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Modo de Exibição")}</h3>
              <p className="text-[13px] text-[#818181] mt-1">
                {translateInterfaceText("Escolha entre um formato de texto ou imagem personalizados.")}</p>
            </div>
        
        {/* OPÇÃO A - TEXTO */}
        <Card className={`border transition-colors ${headlineOverlay.textMode?.enabled ? 'border-[#1D4FFF]/20 bg-white' : 'border-[#E4E7EC] bg-white'}`}>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Type className={`h-5 w-5 transition-colors ${headlineOverlay.textMode?.enabled ? 'text-[#1D4FFF]' : 'text-muted-foreground'}`} />
                <div>
                  <Label className="text-sm font-semibold cursor-pointer">{translateInterfaceText("Modo Texto")}</Label>
                  <p className="text-xs text-muted-foreground">
                    {translateInterfaceText("Personalize headline e subheadline")}</p>
                </div>
              </div>
              <Switch
                checked={headlineOverlay.textMode?.enabled || false}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setLocalHtmlCode('');
                    setHtmlError(null);
                    updateHeadlineOverlayBatch({
                      textMode: { ...headlineOverlay.textMode, enabled: true },
                      htmlMode: { ...headlineOverlay.htmlMode, enabled: false, code: '' },
                      imageMode: { ...headlineOverlay.imageMode, enabled: false },
                    });
                  } else {
                    updateTextMode('enabled', false);
                  }
                }}
              />
            </div>

            {/* Campos só aparecem quando modo texto ativo */}
            {headlineOverlay.textMode?.enabled && (
              <>
                {/* Headline Principal */}
                <div className="space-y-3 pt-4 border-t">
                  <div>
                    <Label className="text-sm font-medium">{translateInterfaceText("Texto da Headline")}</Label>
                    <Input
                      value={localHeadlineText}
                      onChange={(e) => setLocalHeadlineText(e.target.value)}
                      placeholder={translateInterfaceText("Ex: Assista até o final para descobrir o método")}
                      className="mt-2"
                      maxLength={65}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {localHeadlineText.length}/65 caracteres
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">{translateInterfaceText("Tamanho (px)")}</Label>
                      <Input
                        type="number"
                        min={MIN_PX}
                        max={MAX_PX}
                        step={1}
                        value={localHeadlineSize}
                        onChange={(e) => {
                          const str = e.target.value;
                          setLocalHeadlineSize(str);
                          const v = parseInt(str, 10);
                          if (Number.isFinite(v) && v >= MIN_PX && v <= MAX_PX) {
                            updateHeadline('size', v);
                          }
                        }}
                        onBlur={() =>
                          commitSizeOnBlur(localHeadlineSize, 24, (v) => updateHeadline('size', v), setLocalHeadlineSize)
                        }
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">{translateInterfaceText("Fonte")}</Label>
                      <Select value={headlineOverlay.headline.fontFamily} onValueChange={(v) => updateHeadline('fontFamily', v)}>
                        <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Inter">{translateInterfaceText("Inter")}</SelectItem>
                          <SelectItem value="Arial">{translateInterfaceText("Arial")}</SelectItem>
                          <SelectItem value="sans-serif">{translateInterfaceText("Sans-serif")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">{translateInterfaceText("Peso da Fonte")}</Label>
                      <Select value={headlineOverlay.headline.fontWeight || 'normal'} onValueChange={(v) => updateHeadline('fontWeight', v)}>
                        <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">{translateInterfaceText("Normal")}</SelectItem>
                          <SelectItem value="bold">{translateInterfaceText("Negrito")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">{translateInterfaceText("Cor do Texto")}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="mt-2 w-full h-10 flex items-center gap-2.5 px-3 rounded-md border border-input bg-background outline-none transition-all duration-150">
                            <span
                              className="w-5 h-5 rounded-full border border-border flex-shrink-0"
                              style={{ backgroundColor: headlineOverlay.headline.color || '#000000' }}
                            />
                            <span className="text-xs font-mono text-muted-foreground">{(headlineOverlay.headline.color || '#000000').toUpperCase()}</span>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-3" align="start">
                          <HexColorPicker color={headlineOverlay.headline.color || '#000000'} onChange={(c) => updateHeadline('color', c)} />
                          <Input
                            value={headlineOverlay.headline.color || '#000000'}
                            onChange={(e) => updateHeadline('color', e.target.value)}
                            className="mt-2 font-mono text-xs h-8"
                            maxLength={7}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>

                {/* Subheadline (Opcional) */}
                <div className="space-y-3 pt-4 border-t">
                  <div>
                    <Label className="text-sm font-medium">{translateInterfaceText("Texto da Subheadline")}</Label>
                    <Input
                      value={localSubheadlineText}
                      onChange={(e) => setLocalSubheadlineText(e.target.value)}
                      placeholder={translateInterfaceText("Digite o subtítulo")}
                      className="mt-2"
                      maxLength={40}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {localSubheadlineText.length}/40 caracteres
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">{translateInterfaceText("Tamanho (px)")}</Label>
                      <Input
                        type="number"
                        min={MIN_PX}
                        max={MAX_PX}
                        step={1}
                        value={localSubheadlineSize}
                        onChange={(e) => {
                          const str = e.target.value;
                          setLocalSubheadlineSize(str);
                          const v = parseInt(str, 10);
                          if (Number.isFinite(v) && v >= MIN_PX && v <= MAX_PX) {
                            updateSubheadline('size', v);
                          }
                        }}
                        onBlur={() =>
                          commitSizeOnBlur(localSubheadlineSize, 16, (v) => updateSubheadline('size', v), setLocalSubheadlineSize)
                        }
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">{translateInterfaceText("Fonte")}</Label>
                      <Select value={headlineOverlay.subheadline.fontFamily} onValueChange={(v) => updateSubheadline('fontFamily', v)}>
                        <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Inter">{translateInterfaceText("Inter")}</SelectItem>
                          <SelectItem value="Arial">{translateInterfaceText("Arial")}</SelectItem>
                          <SelectItem value="sans-serif">{translateInterfaceText("Sans-serif")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">{translateInterfaceText("Peso da Fonte")}</Label>
                      <Select value={headlineOverlay.subheadline.fontWeight || 'normal'} onValueChange={(v) => updateSubheadline('fontWeight', v)}>
                        <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">{translateInterfaceText("Normal")}</SelectItem>
                          <SelectItem value="bold">{translateInterfaceText("Negrito")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">{translateInterfaceText("Cor do Texto")}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="mt-2 w-full h-10 flex items-center gap-2.5 px-3 rounded-md border border-input bg-background outline-none transition-all duration-150">
                            <span
                              className="w-5 h-5 rounded-full border border-border flex-shrink-0"
                              style={{ backgroundColor: headlineOverlay.subheadline.color || '#555555' }}
                            />
                            <span className="text-xs font-mono text-muted-foreground">{(headlineOverlay.subheadline.color || '#555555').toUpperCase()}</span>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-3" align="start">
                          <HexColorPicker color={headlineOverlay.subheadline.color || '#555555'} onChange={(c) => updateSubheadline('color', c)} />
                          <Input
                            value={headlineOverlay.subheadline.color || '#555555'}
                            onChange={(e) => updateSubheadline('color', e.target.value)}
                            className="mt-2 font-mono text-xs h-8"
                            maxLength={7}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <ButtonTimingCard
          actionButton={{ timing: headlineOverlay.timing }}
          updateTimingProperty={updateTiming}
          labelPrefix="a headline"
        />

        {/* OPÇÃO B - HTML PERSONALIZADO */}
        <Card className={`border transition-colors ${headlineOverlay.htmlMode.enabled ? 'border-[#1D4FFF]/20 bg-white' : 'border-[#E4E7EC] bg-white'}`}>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Code2 className={`h-5 w-5 transition-colors ${headlineOverlay.htmlMode.enabled ? 'text-[#1D4FFF]' : 'text-muted-foreground'}`} />
                <div>
                  <Label className="text-sm font-semibold cursor-pointer">{translateInterfaceText("Modo HTML (Avançado)")}</Label>
                  <p className="text-xs text-muted-foreground">
                    {translateInterfaceText("Indicado para usuários avançados. Apenas HTML simples. Scripts não são permitidos.")}</p>
                </div>
              </div>
              <Switch
                checked={headlineOverlay.htmlMode.enabled}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setLocalHeadlineText('');
                    setLocalSubheadlineText('');
                    updateHeadlineOverlayBatch({
                      htmlMode: { ...headlineOverlay.htmlMode, enabled: true },
                      textMode: { ...headlineOverlay.textMode, enabled: false },
                      imageMode: { ...headlineOverlay.imageMode, enabled: false },
                      headline: { ...headlineOverlay.headline, text: '' },
                      subheadline: { ...headlineOverlay.subheadline, text: '' },
                    });
                  } else {
                    updateHtmlMode('enabled', false);
                  }
                }}
              />
            </div>

            {/* Textarea só aparece quando HTML ativo */}
            {headlineOverlay.htmlMode.enabled && (
              <div className="pt-4 border-t space-y-3">
                <Label className="text-sm font-medium">{translateInterfaceText("Código HTML personalizado")}</Label>
                <Textarea
                  value={localHtmlCode}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLocalHtmlCode(value);
                    updateHtmlMode('code', value);
                    // Validar HTML
                    if (/<script/i.test(value)) {
                      setHtmlError(translateInterfaceText("Scripts não são permitidos por segurança."));
                    } else if (/on\w+\s*=/i.test(value)) {
                      setHtmlError(translateInterfaceText("Event handlers inline não são permitidos."));
                    } else {
                      setHtmlError(null);
                    }
                  }}
                  placeholder="<h1>Meu título personalizado</h1>&#10;Cole seu código HTML aqui..."
                  className={`font-mono text-[13px] h-[180px] max-h-[180px] resize-none overflow-y-auto bg-[#F8F9FB] rounded-[10px] border-[#E4E4E7] p-[14px] mt-2 ${
                    htmlError ? 'border-red-400 focus-visible:ring-red-400' : ''
                  }`}
                />
                {htmlError ? (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{htmlError}</span>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {translateInterfaceText("Insira HTML simples. Scripts não são permitidos por segurança.")}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* OPÇÃO C - IMAGEM */}
        {/* TODO: Recurso "Headline por Dispositivo" será implementado futuramente */}
        {/* <Card className={`border transition-colors ${headlineOverlay.imageMode.enabled ? 'border-[#1D4FFF]/20 bg-white' : 'border-[#E4E7EC] bg-white'}`}>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Image className={`h-5 w-5 transition-colors ${headlineOverlay.imageMode.enabled ? 'text-[#1D4FFF]' : 'text-muted-foreground'}`} />
                <div>
                  <Label className="text-sm font-semibold cursor-pointer">Headline por Dispositivo</Label>
                  <p className="text-xs text-muted-foreground">
                    Personalize a exibição da headline para desktop e mobile de forma independente.
                  </p>
                </div>
              </div>
              <Switch
                checked={headlineOverlay.imageMode.enabled}
                onCheckedChange={(checked) => {
                  if (checked) {
                    updateTextMode('enabled', false);
                    updateHtmlMode('enabled', false);
                    updateImageMode('enabled', true);
                  } else {
                    updateImageMode('enabled', false);
                  }
                }}
              />
            </div>

            {headlineOverlay.imageMode.enabled && (
              <div className="pt-4 border-t space-y-6">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  <Card className="border border-[#E4E7EC] border-dashed bg-white">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Monitor className="h-4 w-4 text-muted-foreground" />
                        <Label className="text-sm font-semibold">Imagem Desktop</Label>
                      </div>

              {!headlineOverlay.imageMode.desktopImage ? (
                <label className="cursor-pointer block">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleDesktopImageUpload(file);
                    }}
                  />
                  <div className="min-h-[120px] bg-[#F6F9FF] rounded-lg border-2 border-dashed border-[#CBD5E1]/60 flex flex-col items-center justify-center p-4 text-center hover:bg-[#EEF4FF] hover:border-[#1D4FFF]/40 transition-all">
                    <div className="h-10 w-10 rounded-full bg-white border border-[#E4E7EC] flex items-center justify-center mb-2">
                      <Image className="h-5 w-5 text-[#1D4FFF]" />
                    </div>
                    <p className="text-xs text-[#64748B]">
                      Clique para enviar
                    </p>
                    <p className="text-[10px] text-[#94A3B8] mt-1">
                      PNG/JPG/GIF • Máx. 2MB
                    </p>
                  </div>
                </label>
              ) : (
                        <div
                          className="relative w-full aspect-video rounded-xl overflow-hidden border border-[#E4E7EC] group cursor-pointer"
                        >
                          <img
                            src={headlineOverlay.imageMode.desktopImage}
                            alt="Desktop"
                            className="w-full h-full object-cover transition-all duration-200 group-hover:brightness-75"
                          />

                          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center gap-4 z-20 rounded-xl">
                            <label className="flex flex-col items-center gap-1.5 group/btn cursor-pointer">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleDesktopImageUpload(file);
                                }}
                              />
                              <div className="w-8 h-8 rounded-full bg-[#1D4FFF] flex items-center justify-center transition-transform duration-200 group-hover/btn:scale-110 shadow-lg">
                                <RefreshCw className="h-4 w-4 text-white" />
                              </div>
                              <span className="text-white text-[10px] font-medium">Trocar</span>
                            </label>

                            <button
                              type="button"
                              onClick={() => updateImageMode('desktopImage', null)}
                              className="flex flex-col items-center gap-1.5 group/btn"
                            >
                              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center transition-transform duration-200 group-hover/btn:scale-110 shadow-lg">
                                <Trash2 className="h-4 w-4 text-white" />
                              </div>
                              <span className="text-white text-[10px] font-medium">Remover</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-dashed">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Smartphone className="h-4 w-4 text-muted-foreground" />
                        <Label className="text-sm font-semibold">Imagem Mobile</Label>
                      </div>

                      {!headlineOverlay.imageMode.mobileImage ? (
                        <label className="cursor-pointer block">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/gif"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleMobileImageUpload(file);
                            }}
                          />
                          <div className="min-h-[120px] bg-[#F6F9FF] rounded-lg border-2 border-dashed border-[#CBD5E1]/60 flex flex-col items-center justify-center p-4 text-center hover:bg-[#EEF4FF] hover:border-[#1D4FFF]/40 transition-all">
                            <div className="h-10 w-10 rounded-full bg-white border border-[#E4E7EC] flex items-center justify-center mb-2">
                              <Image className="h-5 w-5 text-[#1D4FFF]" />
                            </div>
                            <p className="text-xs text-[#64748B]">
                              Clique para enviar
                            </p>
                            <p className="text-[10px] text-[#94A3B8] mt-1">
                              PNG/JPG/GIF • Máx. 2MB
                            </p>
                          </div>
                        </label>
                      ) : (
                        <div
                          className="relative w-full aspect-video rounded-xl overflow-hidden border border-[#E4E7EC] group cursor-pointer"
                        >
                          <img
                            src={headlineOverlay.imageMode.mobileImage}
                            alt="Mobile"
                            className="w-full h-full object-cover transition-all duration-200 group-hover:brightness-75"
                          />

                          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center gap-4 z-20 rounded-xl">
                            <label className="flex flex-col items-center gap-1.5 group/btn cursor-pointer">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleMobileImageUpload(file);
                                }}
                              />
                              <div className="w-8 h-8 rounded-full bg-[#1D4FFF] flex items-center justify-center transition-transform duration-200 group-hover/btn:scale-110 shadow-lg">
                                <RefreshCw className="h-4 w-4 text-white" />
                              </div>
                              <span className="text-white text-[10px] font-medium">Trocar</span>
                            </label>

                            <button
                              type="button"
                              onClick={() => updateImageMode('mobileImage', null)}
                              className="flex flex-col items-center gap-1.5 group/btn"
                            >
                              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center transition-transform duration-200 group-hover/btn:scale-110 shadow-lg">
                                <Trash2 className="h-4 w-4 text-white" />
                              </div>
                              <span className="text-white text-[10px] font-medium">Remover</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </CardContent>
        </Card> */}
      </div>

          {/* TODO: Recurso "Posição" será implementado futuramente */}
          {/* <PositionCard
            selectedPosition={headlineOverlay.position}
            onPositionChange={(position) => updateHeadlineOverlay('position', position as HeadlineOverlay['position'])}
            offsetX={headlineOverlay.offsetX || 0}
            offsetY={headlineOverlay.offsetY || 0}
            onOffsetXChange={(val) => updateHeadlineOverlay('offsetX', val)}
            onOffsetYChange={(val) => updateHeadlineOverlay('offsetY', val)}
          /> */}

        </div>
      </div>
    </div>
  );
};
